import { codec, hash } from 'sjcl';
import uuid from 'uuid4';

import { AD_FRAUD_COOKIE_NAME } from '../constants';
import { CookieUtils } from '../types/cookies_utils';

import Visitor from '../visitor';

const VALID_IDENTIFIER_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

class Identifier {
  constructor(
    private readonly cookieUtils: CookieUtils,
    private readonly firstTouchRevenueForecastingEventEnabled: boolean = true,
  ) {}

  authorizeIdentifier(identifierCookieName: string, hasVisitorCookie: boolean = false) {
    if (!this.isValidIdentifier(this.cookieUtils.getCookie(identifierCookieName))) {
      const revenueForecastingEventTriggerFlag =
        identifierCookieName === AD_FRAUD_COOKIE_NAME && !hasVisitorCookie;
      this.generateAndSaveIdentifier(identifierCookieName, revenueForecastingEventTriggerFlag);
    }
  }

  isValidIdentifier(identifier: string | undefined) {
    return identifier && VALID_IDENTIFIER_REGEX.test(identifier);
  }

  generateUuid() {
    return uuid();
  }

  generateAndSaveIdentifier(
    identifierCookieName: string,
    revenueForecastingEventTriggerFlag: boolean = false,
  ) {
    const identifier = this.generateUuid();
    if (this.firstTouchRevenueForecastingEventEnabled && revenueForecastingEventTriggerFlag) {
      const visitorId = new Visitor(this, this.cookieUtils).getVisitorId();
      if (visitorId) {
        globalThis.GustoAnalytics.triggerRevenueForecastingEvent(visitorId);
      }
    }

    this.cookieUtils.setCookie(identifierCookieName, identifier, 0);
  }

  checkResetIdentifier(userId: string, originHashCookieName: string, identifierCookieName: string) {
    let newIdentifier = null;
    // const visitorIdOriginHash = this.getVisitorIdOriginHash();
    const originIdHash = this.getIdOriginHash(originHashCookieName);
    const visitorPreviouslyIdentified = !!originIdHash;

    const hasUsefulIdentifier = !!userId;

    if (hasUsefulIdentifier || visitorPreviouslyIdentified) {
      const currentIdOrigin = `${userId || ''}`;
      const currentIdOriginHash = codec.base64.fromBits(hash.sha256.hash(currentIdOrigin));

      // Only reset visitor_id if changing from value -> value
      const visitorChanged =
        visitorPreviouslyIdentified && originIdHash !== currentIdOriginHash && !!userId;

      if (visitorChanged) {
        if (hasUsefulIdentifier) {
          this.cookieUtils.setCookie(
            originHashCookieName,
            encodeURIComponent(currentIdOriginHash),
            0,
          );
        } else {
          // Case where user_id is changing from value -> nil
          this.cookieUtils.deleteCookie(originHashCookieName);

          // Remove all user traits, include anonymous_id/visitor_id and user_id from Amptliude
          // local storage so old user_id doesn't propagate to subsequent client-side events
          this.clearAnalyticsUserId();
        }

        this.generateAndSaveIdentifier(identifierCookieName);
        newIdentifier = this.getIdentifier(identifierCookieName);
      } else if (!visitorPreviouslyIdentified) {
        this.cookieUtils.setCookie(
          originHashCookieName,
          encodeURIComponent(currentIdOriginHash),
          0,
        );
      }
    }

    return newIdentifier;
  }

  clearAnalyticsUserId() {
    // Clear user trait cache
    if (globalThis.analytics) {
      globalThis.analytics.reset();
    }
  }

  getIdentifier(identifierCookieName: string) {
    // This does nothing if valid vi cookie has been set already
    this.authorizeIdentifier(identifierCookieName);

    return this.cookieUtils.getCookie(identifierCookieName);
  }

  getIdOriginHash(originHashCookieName: string) {
    const cookie = this.cookieUtils.getCookie(originHashCookieName);

    return cookie && decodeURIComponent(cookie);
  }
}

export default Identifier;
